import React, {useState} from 'react'
import type {ToolCallMessagePartProps} from '@assistant-ui/react'
import {formatBytes} from '@/shared/utils'
import {useImports} from '@/shared/utils/hooks/useAPIFiles'
import {useLayoutPanelsOptional} from '@/shared/context/ChatContext'
import {tryParseCloudPath} from '@/shared/utils/cloudPath'
import type {ImportRecord} from '../../core/importModels'
import {CloudPathLink} from '../ai-chat/cloudPathLinks'
import {OmicsToolCollapsibleRow} from '../ai-chat/omicsToolCollapsibleRow'
import {formatToolDisplayLabel} from '../ai-chat/toolDisplayLabels'
import {formatIncompleteErrorText} from '../ai-chat/workingSummary'
import {useFileImportActions} from '../FileImportActionsContext'
import {importProgressText, IMPORT_STATUS_LABEL, isImportActive} from '../imports/importStatus'

/**
 * Chat cards for the agent's `start_transfer` / `get_transfer_status` (omicsai-bioagent
 * tools/transfers.py). The tool result is JSON text:
 * - a started transfer: `{import_id, run_id, destination, sources, message}`;
 * - files on the user's computer: `{status: "needs_upload", destination, files, message}`;
 * - a status: `{import_id, status, destination, copied_files, failed_files, bytes_copied, errors}`.
 * A started transfer shows live progress from the same import record as the Transfers list.
 */

export interface TransferToolResult {
  status?: string
  import_id?: string
  destination?: string
  sources?: string[]
  files?: string[]
  message?: string
  copied_files?: number
  failed_files?: number
  total_files?: number
  bytes_copied?: number
  errors?: Array<{source?: string; error?: string}>
}

const IMPORT_STATUSES: ReadonlySet<string> = new Set(['queued', 'running', 'succeeded', 'partial', 'failed'])

function stringOrUndefined(value: unknown): string | undefined {
  return typeof value === 'string' && value.trim() ? value : undefined
}

function stringList(value: unknown): string[] | undefined {
  return Array.isArray(value) ? value.filter((v): v is string => typeof v === 'string') : undefined
}

function numberOrUndefined(value: unknown): number | undefined {
  return typeof value === 'number' && Number.isFinite(value) ? value : undefined
}

/**
 * The tool's JSON result, field by field (a malformed field is dropped, never trusted), or null for
 * anything that isn't a JSON object (an error message, an older format).
 */
export function parseTransferResult(result: unknown): TransferToolResult | null {
  if (typeof result !== 'string' || !result.trim().startsWith('{')) return null
  let raw: unknown
  try {
    raw = JSON.parse(result)
  } catch {
    return null
  }
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null
  const r = raw as Record<string, unknown>
  const errors = Array.isArray(r.errors)
    ? r.errors
        .filter((e): e is Record<string, unknown> => Boolean(e) && typeof e === 'object')
        .map((e) => ({source: stringOrUndefined(e.source), error: stringOrUndefined(e.error)}))
    : undefined
  return {
    status: stringOrUndefined(r.status),
    import_id: stringOrUndefined(r.import_id),
    destination: stringOrUndefined(r.destination),
    sources: stringList(r.sources),
    files: stringList(r.files),
    message: stringOrUndefined(r.message),
    copied_files: numberOrUndefined(r.copied_files),
    failed_files: numberOrUndefined(r.failed_files),
    total_files: numberOrUndefined(r.total_files),
    bytes_copied: numberOrUndefined(r.bytes_copied),
    errors,
  }
}

/** An import status the card knows, or undefined (an unknown string is shown, never styled). */
function knownImportStatus(status: string | undefined): ImportRecord['status'] | undefined {
  return status && IMPORT_STATUSES.has(status) ? (status as ImportRecord['status']) : undefined
}

type TransferToolProps = ToolCallMessagePartProps<Record<string, unknown>, string>

function argDestination(args: unknown): string {
  const value = args && typeof args === 'object' ? (args as Record<string, unknown>).destination : undefined
  return typeof value === 'string' && value.trim() ? value.trim() : 'cloud:'
}

/**
 * A transfer's live record, while it is in the user's recent imports (older than 7 days and
 * dismissed ones drop out). `loading` only until the list has been read once.
 */
function useImportRecord(importId: string | undefined): {record?: ImportRecord; loading: boolean} {
  const {data, isLoading} = useImports({enabled: Boolean(importId)})
  return {
    record: importId ? data?.find((r) => r.importId === importId) : undefined,
    loading: Boolean(importId) && isLoading,
  }
}

const Destination: React.FC<{path: string}> = ({path}) =>
  tryParseCloudPath(path) ? <CloudPathLink path={path}>{path}</CloudPathLink> : <>{path}</>

/** Files on the user's computer: offer the upload into the destination folder. */
const NeedsUpload: React.FC<{result: TransferToolResult}> = ({result}) => {
  const layout = useLayoutPanelsOptional()
  const actions = useFileImportActions()
  const [folderOpened, setFolderOpened] = useState(false)
  const destination = result.destination ?? 'cloud:'
  const parsedDestination = tryParseCloudPath(destination)

  const upload = () => {
    if (!parsedDestination) return
    layout?.openFilesPanelAt(parsedDestination.rel)
    // The file dialog opens now; the files go to the folder Files shows when they are picked, which
    // by then is the destination. Without a mounted Files panel there is no upload input yet.
    if (actions) actions.onUploadFile()
    else setFolderOpened(true)
  }

  return (
    <div className='omics-transfer-card' data-testid='omics-transfer-needs-upload'>
      <p className='mb-2'>
        These files are on your computer. Upload them into <Destination path={destination} />:
      </p>
      {result.files && result.files.length > 0 && (
        <ul className='omics-transfer-card__files'>
          {result.files.map((file) => (
            <li key={file}>{file}</li>
          ))}
        </ul>
      )}
      <button type='button' className='btn btn-sm btn-primary' onClick={upload} disabled={!parsedDestination}>
        <i className='fas fa-file-arrow-up me-2' aria-hidden='true' />
        Upload from your computer
      </button>
      {!parsedDestination && (
        <p className='mt-2 mb-0 text-danger' role='status'>
          {destination} is not a valid folder, so the upload can't start here. Use Import Data → Upload Files
          in the Files panel.
        </p>
      )}
      {folderOpened && (
        <p className='mt-2 mb-0 text-muted' role='status'>
          The folder is open in Files: use Import Data → Upload Files there.
        </p>
      )}
    </div>
  )
}

/** Status, progress and errors of a transfer: live record first, the tool's own answer otherwise. */
const TransferDetails: React.FC<{result: TransferToolResult; record?: ImportRecord}> = ({result, record}) => {
  const destination = result.destination ?? 'cloud:'
  const errors = record?.errors ?? result.errors ?? []
  const bytes = record?.bytesCopied ?? result.bytes_copied ?? 0
  return (
    <div className='omics-transfer-card' data-testid='omics-transfer-details'>
      <div>
        To <Destination path={destination} />
        {record && (
          <>
            {' · '}
            {importProgressText(record)}
          </>
        )}
        {bytes > 0 && ` · ${formatBytes(bytes)}`}
      </div>
      {result.sources && result.sources.length > 0 && (
        <ul className='omics-transfer-card__files'>
          {result.sources.slice(0, 5).map((source) => (
            <li key={source}>{source}</li>
          ))}
          {result.sources.length > 5 && <li>+{result.sources.length - 5} more</li>}
        </ul>
      )}
      {record?.runner === 'fargate' && isImportActive(record.status) && (
        <div className='text-muted'>{'Large file (>1GB) can take up to a minute to start import'}</div>
      )}
      {errors.length > 0 && (
        <ul className='omics-transfer-card__errors'>
          {errors.map((e, i) => (
            <li key={i}>
              {e.source ? `${e.source}: ` : ''}
              {e.error}
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/** Card for both transfer tools (registered in toolUIs.tsx). */
export function TransferToolCard({toolName, args, result, status, isError}: TransferToolProps): React.ReactElement {
  const parsed = parseTransferResult(result)
  const {record, loading} = useImportRecord(parsed?.import_id)
  const [open, setOpen] = useState(false)
  // The upload request is the card's call to action: open until the user closes it, also when the
  // result only arrives after the card was first rendered as "running".
  const [uploadOpen, setUploadOpen] = useState(true)
  const label = formatToolDisplayLabel(toolName)
  const toolPending = status.type === 'running'
  const toolFailed = Boolean(isError) || status.type === 'incomplete'

  if (toolPending || toolFailed || !parsed) {
    const errorText = toolFailed ? formatIncompleteErrorText(status) || result : undefined
    return (
      <OmicsToolCollapsibleRow
        className='omics-aui-tool omics-aui-tool-transfer'
        title={label}
        subtitle={argDestination(args)}
        pending={toolPending}
        failed={toolFailed}
        open={open}
        onToggle={() => setOpen((v) => !v)}
      >
        {errorText ? <div className='alert alert-danger py-2 mb-0'>{errorText}</div> : result}
      </OmicsToolCollapsibleRow>
    )
  }

  if (parsed.status === 'needs_upload') {
    return (
      <OmicsToolCollapsibleRow
        className='omics-aui-tool omics-aui-tool-transfer'
        title='Upload needed'
        subtitle={parsed.destination}
        open={uploadOpen}
        onToggle={() => setUploadOpen((v) => !v)}
      >
        <NeedsUpload result={parsed} />
      </OmicsToolCollapsibleRow>
    )
  }

  // Live state comes from the import record. Without one — not loaded yet, older than 7 days,
  // dismissed — the tool's own answer is a snapshot, never shown as still running.
  const importStatus = record?.status ?? knownImportStatus(parsed.status)
  const active = record ? isImportActive(record.status) : loading
  const statusLabel = importStatus
    ? IMPORT_STATUS_LABEL[importStatus]
    : parsed.status ?? (parsed.import_id ? 'Started' : undefined)
  return (
    <OmicsToolCollapsibleRow
      className='omics-aui-tool omics-aui-tool-transfer'
      title={statusLabel ? `${label}: ${statusLabel}` : label}
      subtitle={parsed.destination}
      meta={record ? importProgressText(record) : undefined}
      pending={active}
      failed={importStatus === 'failed' || importStatus === 'partial'}
      open={open}
      onToggle={() => setOpen((v) => !v)}
    >
      <TransferDetails result={parsed} record={record} />
    </OmicsToolCollapsibleRow>
  )
}
